import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SeriesData } from './SeriesData';
import { seriesDelete, seriesListApi } from '../../api/HttpApis';
import { showToast } from "../../utils/Toastify/Toastify";
import ListView from '../../component/ListView/ListView';
import { contentStats, contentBadge } from '../../component/ListView/contentCard';
import { trimStr } from '../../utils/theme/ShortenedText/ShortenedText';
import { useSearch } from '../../context/SearchContext';
import FloatingButton from '../../component/FloatingButton/FloatingButton';
import { useRbac } from '../../rbac/RbacContext';

// Top tab bar filtering by review status (same as the Content page). A series'
// status is its worst episode's -- see SeriesController.list.
const QC_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "Approved", label: "Approved" },
  { key: "Pending", label: "Pending" },
];

const Series = () => {
  const navigate = useNavigate();
  // Only a Creator gets the tabs, to track their own submissions' review
  // status -- Admin reviews everything via the Submissions page.
  const isCreator = localStorage.getItem("role") === "Creator";
  const [qcStatus, setQcStatus] = useState("all");
  const qcStatusParam = qcStatus === "all" ? "" : qcStatus;
  const { can } = useRbac();
  // Server also enforces this via requirePermission on POST /series/create.
  const canCreateSeries = can("series", "create");
  const [seriesData, setSeriesData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [seasonLoaderId, setSeasonLoaderId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCreator, setSelectedCreator] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedGenre, setSelectedGenre] = useState();
  const [selectedSeriesFormat, setSelectedSeriesFormat] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const { searchQuery, setSearchQuery } = useSearch();
  const prevSearchQuery = useRef(searchQuery);
  const fetchData = async (page, limit) => {
    if (searchQuery) return;
    setLoading(true);
    try {
      const data = await SeriesData(
        selectedDate,
        selectedCreator,
        selectedRating,
        selectedGenre,
        page,
        limit,
        selectedSeriesFormat,
        qcStatusParam
      );
      setSeriesData(data || []);
      setTotalCount(Array.isArray(data) && data.length > 0 ? data[0].totalCount || 0 : 0);
    } catch (err) {
      setSeriesData([]);
      showToast("Failed to fetch series list.", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = (id) => {
    setEditingId(id);
    navigate(`/Addseries/${id}`);
  };
  const handleSeasons = (id) => {
    setSeasonLoaderId(id);
    setSearchQuery(null);
    setTimeout(() => navigate(`/mainlayout/seasons/${id}`), 300);
  };
  const handleAddClick = () => {
    navigate(`/Addseries`);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this series?');
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await seriesDelete(id);
      setSeriesData((prev) => prev.filter((item) => item.id !== id));
      // Keep the Total tile in step without a refetch.
      setTotalCount((c) => Math.max(0, (Number(c) || 0) - 1));
      showToast(response.data.message, 'success');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete series.', 'error');
    } finally {
      setDeletingId(null);
    }
  };
  const handleTabChange = (key) => {
    setQcStatus(key);
    handleFilterChange("qcStatus", key === "all" ? "" : key);
  };

  const handleFilterChange = async (type, value, page, perpage) => {
    if (type === "genre") setSelectedGenre(value);
    if (type === "date") setSelectedDate(value);
    if (type === "creator") setSelectedCreator(value);
    if (type === "rating") setSelectedRating(value);
    if (type === "seriesFormat") setSelectedSeriesFormat(value);

    const updatedGenre = type === "genre" ? value : selectedGenre;
    const updatedDate = type === "date" ? value : selectedDate;
    const updatedCreator = type === "creator" ? value : selectedCreator;
    const updatedRating = type === "rating" ? value : selectedRating;
    const updatedSeriesFormat = type === "seriesFormat" ? value : selectedSeriesFormat;
    const updatedQcStatus = type === "qcStatus" ? value : qcStatusParam;
    setLoading(true);
    try {
      const response = await seriesListApi(
        {},
        updatedDate,
        updatedCreator,
        updatedRating,
        updatedGenre,
        perpage,
        page,
        searchQuery,
        updatedSeriesFormat,
        updatedQcStatus
      );

      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const formattedData = data.map((item) => ({
        id: item.id,
        name: trimStr(item.name),
        media: item.portrait,
        status: item.status,
        qc_status: item.qc_status,
        rating: item.rating,
        portrait_url: item.portrait,
        landscape_url: item.landscape,
        access: item.access,
        buttonText: "Edit",
        buttonText2: "Remove",
        buttonText3: "Seasons",
      }));

      setSeriesData(formattedData);
      setTotalCount(response?.data?.total);
      showToast();
    } catch (error) {
      showToast("Failed to apply filter", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (prevSearchQuery.current === searchQuery) return;
    prevSearchQuery.current = searchQuery;

    const timer = setTimeout(() => {
      if (searchQuery === "" || searchQuery) {
        handleFilterChange("search", "");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const cardFor = (item) => ({
    id: item.id,
    title: item.name,
    image: item.media,
    meta: [item.rating, item.access].filter((v) => v && v !== "N/A").join(" · "),
    badge: contentBadge(item),
    onClick: () => handleSeasons(item.id),
    actions: [
      { icon: "edit", label: "Edit", onClick: () => handleEdit(item.id) },
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === item.id,
        onClick: () => handleDelete(item.id),
      },
    ],
  });
  const stats = contentStats(seriesData, totalCount);

  return (
    <div>
      <ListView
        heading="Series"
        data={seriesData}
        getCard={cardFor}
        stats={stats}
        button={true}
        handleFilterChange={handleFilterChange}
        selectedDate={selectedDate}
        selectedRating={selectedRating}
        selectedGenre={selectedGenre}
        selectedSeriesFormat={selectedSeriesFormat}
        tabs={isCreator ? QC_STATUS_TABS : []}
        activeTab={qcStatus}
        onTabChange={handleTabChange}
        totalCount={totalCount}
        tableLoading={loading}
        onPageChange={fetchData}
        addLabel="Add Series"
        onAdd={canCreateSeries ? handleAddClick : undefined}
      />
      <FloatingButton />
    </div>
  );
};

export default Series;